// biome-ignore lint/performance/noNamespaceImport: xlsx package exposes read/utils via namespace
import * as XLSX from "xlsx";

const MAX_PREVIEW_ROWS = 5;

export type ParsedSpreadsheetResult = {
	headers: string[];
	rows: Record<string, string>[];
};

/**
 * Lê arquivo CSV, XLSX ou XLS no browser e retorna cabeçalhos e até 5 linhas
 * no mesmo formato da API /api/expenses/import/header.
 */
export async function parseSpreadsheetFile(
	file: File
): Promise<ParsedSpreadsheetResult> {
	const buffer = await readFileAsArrayBuffer(file);
	const workbook = XLSX.read(buffer, { type: "array" });

	const firstSheetName = workbook.SheetNames[0];
	if (!firstSheetName) {
		throw new Error("A planilha não contém abas.");
	}

	const worksheet = workbook.Sheets[firstSheetName];

	const worksheetRef = worksheet["!ref"];
	if (!worksheetRef) {
		throw new Error("A planilha está vazia.");
	}

	const decodedRange = XLSX.utils.decode_range(worksheetRef);
	const previewEndRow = Math.min(decodedRange.e.r, MAX_PREVIEW_ROWS);
	const previewRange: XLSX.Range = {
		s: { r: decodedRange.s.r, c: decodedRange.s.c },
		e: { r: previewEndRow, c: decodedRange.e.c },
	};

	const raw = XLSX.utils.sheet_to_json<string[]>(worksheet, {
		header: 1,
		defval: "",
		raw: false,
		range: previewRange,
	});

	if (!raw.length) {
		throw new Error("A planilha está vazia.");
	}

	const headerRow = raw[0];
	const headers = headerRow.map((cell) => String(cell ?? "").trim());

	if (headers.every((h) => !h)) {
		throw new Error("Nenhum cabeçalho encontrado na primeira linha.");
	}

	const dataRows = raw.slice(1, 1 + MAX_PREVIEW_ROWS);
	const rows: Record<string, string>[] = [];

	for (const row of dataRows) {
		const rowObj: Record<string, string> = {};
		for (let i = 0; i < headers.length; i++) {
			const key = headers[i];
			if (!key) {
				continue;
			}
			const value = row[i];
			rowObj[key] =
				value === null || value === undefined ? "" : String(value).trim();
		}
		rows.push(rowObj);
	}

	return { headers, rows };
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result;
			if (result instanceof ArrayBuffer) {
				resolve(result);
			} else {
				reject(new Error("Falha ao ler o arquivo."));
			}
		};
		reader.onerror = () => {
			reject(new Error("Falha ao ler o arquivo."));
		};
		reader.readAsArrayBuffer(file);
	});
}
